import { getCanonicalBrowsePathForPathname, parseLegacyCategoryPath } from './browsePath';
import { getLegacyBrowseCanonicalRedirectPath } from './legacyQuadBrowseRedirect';

/**
 * Splits an href into the pathname to canonicalize and the `?query#hash` tail to carry through
 * untouched. Editorial hrefs reaching here carry campaign attribution (`utm_campaign=…`), so the
 * tail has to survive normalization.
 */
const splitHref = (href: string): { pathname: string; tail: string } => {
    const trimmed = href.trim();

    if (trimmed.includes('://')) {
        try {
            const url = new URL(trimmed);
            return { pathname: url.pathname, tail: `${url.search}${url.hash}` };
        } catch {
            return { pathname: trimmed, tail: '' };
        }
    }

    const tailStart = trimmed.search(/[?#]/);
    const pathname = tailStart === -1 ? trimmed : trimmed.slice(0, tailStart);
    const tail = tailStart === -1 ? '' : trimmed.slice(tailStart);

    return { pathname: pathname.startsWith('/') ? pathname : `/${pathname}`, tail };
};

const ensureTrailingSlash = (path: string): string => (path.endsWith('/') ? path : `${path}/`);

/**
 * Normalizes category tile / breadcrumb hrefs from legacy `/c/{slug}/{id}/` (or full URLs)
 * to slug-only canonical browse paths used elsewhere in the app.
 */
export const getCanonicalBrowsePathFromHref = (href: string): string => {
    if (!href) {
        return href;
    }

    const { pathname: rawPathname, tail } = splitHref(href);
    const pathname = ensureTrailingSlash(rawPathname);

    const browseCanonical = getCanonicalBrowsePathForPathname(pathname);
    if (browseCanonical) {
        return `${browseCanonical}${tail}`;
    }

    const legacyQuadCanonical = getLegacyBrowseCanonicalRedirectPath(pathname);
    if (legacyQuadCanonical) {
        return `${legacyQuadCanonical}${tail}`;
    }

    const legacyParsed = parseLegacyCategoryPath(pathname);
    if (legacyParsed) {
        return `${legacyParsed.base}/${legacyParsed.categorySlug}/${tail}`;
    }

    return `${pathname}${tail}`;
};
